import { AppGroupType, APPS_LIMIT, SortType } from '@wix/app-market-services';
import type {
  AppGroupSection,
  Services,
  QueryAppsFilter,
} from '@wix/app-market-services';
import type { QueryAppsResponse } from '../../components/apps-section';

export interface CollectionPageHeaderProps {
  slug: string;
  primaryTitle: string;
  secondaryTitle: string;
  description: string;
}

export async function getCollectionPageHeaderData({
  services,
  slug,
  languageCode,
}: {
  services: Services;
  slug: string;
  languageCode: string;
}): Promise<CollectionPageHeaderProps> {
  const tags = await services.queryTag({
    slugs: [slug],
    languageCode,
    isHidden: false,
  });
  const tag = tags.findBySlug(slug);
  if (tag.id === '') {
    throw new Error(
      `Collection with slug ${slug} not found, languageCode: ${languageCode}`,
    );
  }

  return {
    slug,
    primaryTitle: tag.name,
    secondaryTitle: tag.collectionTeaser,
    description: tag.description,
  };
}

export async function queryApps({
  services,
  slug,
  languageCode,
  offset = 0,
  filter,
}: {
  services: Services;
  slug: string;
  languageCode: string;
  offset?: number;
  filter?: QueryAppsFilter;
}): Promise<QueryAppsResponse> {
  const tags = await services.queryTag({
    slugs: [slug],
    languageCode,
    isHidden: false,
  });
  const tag = tags.findBySlug(slug);
  if (tag.id === '') {
    throw new Error(
      `Collection with slug ${slug} not found, languageCode: ${languageCode}`,
    );
  }

  const tagId = tag.id;

  const { appIds, hasMoreApps, totalApps } =
    await services.appMarketClientAggregator.enhancedSearchApps({
      sortTypes: [SortType.DEALER],
      paging: {
        limit: APPS_LIMIT,
        offset,
      },
      filter: { ...filter, tagId },
    });

  const appsData = await services.getAppsByAppIds({
    appIds,
    status: 'PUBLISHED',
  });
  const apps = appsData.toJSON();

  return {
    appGroup: {
      type: AppGroupType.COLLECTION,
      tagSlug: slug,
      apps: apps as AppGroupSection['apps'],
      title: '',
      totalAppsCount: 0,
    },
    paging: {
      total: totalApps,
      hasNext: hasMoreApps,
    },
    collectionName: tag.name,
  };
}
